import React from 'react';

import {format} from 'date-fns';
import {useUnit} from 'effector-react';
import {useTranslation} from 'react-i18next';

import {EMPTY_VALUE as EMPTY} from '@consts/common';
import {auditViolationModel} from '@entities/audit-violation/model';
import {LocalizedTitle} from '@entities/violations/types';
import {formatNumberWithFraction} from '@utils/number';

import {
    InfoLabel,
    InfoList,
    InfoValue,
    SectionCard,
    SectionGrid,
    SectionTitle,
    Wrapper,
} from './styled';

export const localizeViolationTitle = (
    title: LocalizedTitle | null | undefined,
    language: string,
) => {
    if (!title) return EMPTY;
    return title[language] ?? title.en ?? Object.values(title)[0] ?? EMPTY;
};

export const formatViolationDateTime = (value: string | null | undefined) => {
    if (!value) return EMPTY;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return EMPTY;
    return format(date, 'dd.MM.yyyy HH:mm');
};

export const formatViolationMoney = (value: number | string | null | undefined) => {
    if (value == null || value === '') return EMPTY;

    const normalized = typeof value === 'string' ? value.replace(/[\s,]/g, '') : value;
    const number = Number(normalized);

    return Number.isFinite(number) ? formatNumberWithFraction(number) : EMPTY;
};

export const formatViolationTaxPeriod = (value: number | null | undefined) => {
    if (!value) return EMPTY;
    const raw = String(value);
    if (raw.length < 6) return raw;

    const year = Number(raw.slice(0, 4));
    const month = Number(raw.slice(4, 6)) - 1;
    if (Number.isNaN(year) || Number.isNaN(month) || month < 0 || month > 11) return raw;

    return format(new Date(year, month, 1), 'MMMM yyyy');
};

const InfoRow = ({label, value}: {label: string; value: React.ReactNode}) => (
    <>
        <InfoLabel>{label}</InfoLabel>
        <InfoValue>{value}</InfoValue>
    </>
);

export const ViolationGeneralInformation = () => {
    const {t, i18n} = useTranslation();
    const {violation, violationTypes} = useUnit(auditViolationModel.state);

    const violationType = violationTypes.find((item) => item.id === violation?.type);

    return (
        <Wrapper>
            <SectionGrid>
                <SectionCard>
                    <SectionTitle>{t('page_audit.terms.violation')}</SectionTitle>
                    <InfoList>
                        <InfoRow
                            label={t('page_audit.terms.violation-type')}
                            value={localizeViolationTitle(violationType?.title, i18n.language)}
                        />
                        <InfoRow
                            label={t('page_audit.page_violation.date-of-detection')}
                            value={formatViolationDateTime(violation?.created)}
                        />
                        <InfoRow
                            label={t('page_audit.page_violation.date-of-update')}
                            value={formatViolationDateTime(violation?.updated)}
                        />
                        <InfoRow
                            label={t('page_audit.terms.tax-period')}
                            value={formatViolationTaxPeriod(violation?.taxPeriod)}
                        />
                    </InfoList>
                </SectionCard>

                <SectionCard>
                    <SectionTitle>{t('page_audit.terms.sum')}</SectionTitle>
                    <InfoList>
                        <InfoRow
                            label={t('page_audit.terms.vat-sum-total-by-nd')}
                            value={formatViolationMoney(violation?.declVatSum)}
                        />
                        <InfoRow
                            label={t('page_audit.terms.vat-sum-total-by-sf')}
                            value={formatViolationMoney(violation?.invVatSum)}
                        />
                        <InfoRow
                            label={t('page_audit.terms.tax-gap')}
                            value={formatViolationMoney(violation?.gapSum)}
                        />
                        <InfoRow
                            label={t('page_audit.terms.payment-amount')}
                            value={formatViolationMoney(violation?.paySum)}
                        />
                    </InfoList>
                </SectionCard>
            </SectionGrid>
        </Wrapper>
    );
};
